import { useCallback } from 'react';

import { getWindowSafe } from '@change-corgi/core/window';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { useCopyLinkButton, useSharePetitionButton } from '#src/app/pages/starterDashboardV2/shared/hooks';

import { useShareUrl } from '../../../../../../../PageBody/components/GrowPetition/components/ShareYourPetition/components/ShareUrl/hooks/useShareUrl';

export type UseActionButtonsResult = ModelHookResult<
	{
		copied: boolean;
	},
	{
		handleEditPetitionClick: () => void;
		handleViewPetitionClick: () => void;
		handleCopyLinkClick: () => void;
		handleSharePetitionClick: () => Promise<void>;
	}
>;

export const EDIT_CLICK_EVENT_NAME = 'petition_manage_edit_click';
export const VIEW_CLICK_EVENT_NAME = 'petition_manage_view_click';

export function useActionButtons(): UseActionButtonsResult {
	const {
		data: {
			petition: {
				signatureState: {
					signatureCount: { displayed: signatureCount },
				},
				slug,
			},
		},
	} = useStarterDashboardV2PageData();
	const {
		data: { shareUrl },
	} = useShareUrl();
	const {
		data: { copied },
		actions: { handleShare },
	} = useCopyLinkButton({ shareUrl });
	const {
		actions: { handleShareClick },
	} = useSharePetitionButton();
	const track = useTracking();
	const window = getWindowSafe();

	const trackAndNewWindow = useCallback(
		(eventName: string, url: string) => () => {
			// Track the event
			void track(eventName, { num_signatures: signatureCount });
			// Open the URL in a new window/tab
			if (window) {
				window.open(url, '_blank');
			}
		},
		[window, signatureCount, track],
	);

	return {
		data: {
			copied,
		},
		actions: {
			handleEditPetitionClick: trackAndNewWindow(EDIT_CLICK_EVENT_NAME, `/p/${encodeURIComponent(slug)}/edit`),
			handleViewPetitionClick: trackAndNewWindow(VIEW_CLICK_EVENT_NAME, `/p/${encodeURIComponent(slug)}`),
			handleCopyLinkClick: handleShare,
			handleSharePetitionClick: async () =>
				handleShareClick({
					source_location: 'petition_dashboard_share_modal',
					view_context: 'petition_dashboard_share_modal',
				}),
		},
	};
}
